import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { brand } from '../../config/brand';
import {
    MessageSquare,
    Send,
    Clock,
    CheckCircle2,
    AlertCircle,
    User as UserIcon,
    Info,
    ChevronDown,
    Search,
    Filter,
    Mail,
    Loader2
} from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from 'sonner';
import { cn } from "../../lib/utils";

interface SupportRequest {
    id: string;
    userId: string | null;
    name: string | null;
    email: string | null;
    category: string;
    message: string;
    status: 'PENDING' | 'OPEN' | 'CLOSED' | 'IN_PROGRESS';
    createdAt: string;
    user?: {
        name: string | null;
        email: string;
        tier: string;
    }
}

export default function SupportTickets() {
    const [tickets, setTickets] = useState<SupportRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<string>('ALL');
    const [replyingTo, setReplyingTo] = useState<SupportRequest | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/admin/support-requests`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(response.data);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            toast.error('Failed to load support tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleSendReply = async () => {
        if (!replyingTo || !replyMessage.trim()) return;

        try {
            setSendingReply(true);
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/admin/support-requests/${replyingTo.id}/reply`, {
                message: replyMessage,
                status: 'CLOSED'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Reply sent successfully');
            setReplyingTo(null);
            setReplyMessage('');
            fetchTickets(); // Refresh
        } catch (error) {
            console.error('Failed to send reply:', error);
            toast.error('Failed to send email reply');
        } finally {
            setSendingReply(false);
        }
    };

    const handleStatusUpdate = async (id: string, status: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/admin/support-requests/${id}/status`, {
                status
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Ticket marked as ${status}`);
            fetchTickets();
        } catch (error) {
            toast.error('Failed to update ticket status');
        }
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch =
            t.message.toLowerCase().includes(search.toLowerCase()) ||
            t.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.email?.toLowerCase().includes(search.toLowerCase()) ||
            t.user?.email.toLowerCase().includes(search.toLowerCase());

        const matchesFilter = filter === 'ALL' || t.status === filter;

        // Hide closed tickets unless showing archived or filtering specifically for closed
        const matchesArchive = showArchived || t.status !== 'CLOSED' || filter === 'CLOSED';

        return matchesSearch && matchesFilter && matchesArchive;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-card p-6 rounded-2xl border border-border shadow-xl">
                <div>
                    <h2 className="text-2xl font-black text-foreground flex items-center gap-3">
                        <MessageSquare className="h-6 w-6 text-primary" />
                        Support Tickets
                    </h2>
                    <p className="text-muted-foreground text-sm mt-1">Manage user requests and feedback</p>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search tickets..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 bg-muted border-border text-foreground"
                        />
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="border-border bg-muted text-foreground">
                                <Filter className="mr-2 h-4 w-4" />
                                {filter === 'ALL' ? 'All Status' : filter}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-card border-border text-foreground">
                            <DropdownMenuItem onClick={() => setFilter('ALL')}>All</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilter('PENDING')}>Pending</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilter('OPEN')}>Open</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilter('IN_PROGRESS')}>In Progress</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFilter('CLOSED')}>Closed</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                        variant="outline"
                        onClick={() => setShowArchived(!showArchived)}
                        className={cn(
                            "border-border bg-muted",
                            showArchived ? "text-primary border-primary/30" : "text-muted-foreground"
                        )}
                    >
                        {showArchived ? "Hide Archived" : "Show Archived"}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                    <p className="text-muted-foreground font-medium">Loading tickets...</p>
                </div>
            ) : filteredTickets.length === 0 ? (
                <div className="bg-card border border-border rounded-2xl p-20 text-center space-y-4 shadow-xl">
                    <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                        <MessageSquare className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">No tickets found</h3>
                    <p className="text-muted-foreground max-w-xs mx-auto">Either there are no support requests yet or your filters are too specific.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {filteredTickets.map((ticket) => (
                        <div
                            key={ticket.id}
                            className={cn(
                                "group bg-card rounded-2xl border transition-all overflow-hidden shadow-lg",
                                ticket.status === 'OPEN' ? "border-primary/30 bg-primary/5" : "border-border hover:border-border"
                            )}
                        >
                            <div className="p-6">
                                <div className="flex flex-col md:flex-row justify-between gap-4 items-start">
                                    <div className="space-y-1 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge className={cn(
                                                "text-[10px] font-black tracking-widest uppercase",
                                                ticket.status === 'PENDING' ? "bg-amber-500/20 text-amber-500 border-amber-500/20" :
                                                    ticket.status === 'OPEN' ? "bg-primary/20 text-primary border-primary/20" :
                                                        ticket.status === 'IN_PROGRESS' ? "bg-purple-500/20 text-purple-400 border-purple-500/20" :
                                                            "bg-green-500/10 text-green-500 border-green-500/20"
                                            )}>
                                                {ticket.status}
                                            </Badge>
                                            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground font-bold uppercase">
                                                {ticket.category}
                                            </Badge>
                                            <span className="text-[10px] text-muted-foreground font-medium flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(ticket.createdAt).toLocaleString()}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 pt-2">
                                            <div className="h-8 w-8 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground shrink-0">
                                                <UserIcon className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <p className="text-foreground font-bold text-sm">{ticket.name || ticket.user?.name || 'Guest User'}</p>
                                                <p className="text-muted-foreground text-xs">{ticket.email || ticket.user?.email}</p>
                                            </div>
                                            {ticket.user?.tier && (
                                                <Badge className="ml-2 bg-primary/20 text-primary text-[9px] border-none">
                                                    {ticket.user.tier}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground">
                                                    Status <ChevronDown className="ml-1 h-3 w-3" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="bg-card border-border text-foreground">
                                                <DropdownMenuItem onClick={() => handleStatusUpdate(ticket.id, 'PENDING')}>Mark as Pending</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleStatusUpdate(ticket.id, 'OPEN')}>Mark as Open</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleStatusUpdate(ticket.id, 'IN_PROGRESS')}>Mark as In Progress</DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleStatusUpdate(ticket.id, 'CLOSED')}>Mark as Closed</DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        <Button
                                            size="sm"
                                            className="bg-primary hover:bg-primary/90 text-white font-bold h-8 flex items-center gap-1.5"
                                            onClick={() => {
                                                setReplyingTo(ticket);
                                                setReplyMessage('');
                                            }}
                                        >
                                            <Mail className="h-3.5 w-3.5" />
                                            Reply
                                        </Button>
                                    </div>
                                </div>

                                <div className="mt-4 p-4 bg-muted/50 rounded-xl border border-border/50 leading-relaxed text-foreground text-sm whitespace-pre-wrap italic">
                                    "{ticket.message}"
                                </div>
                            </div>

                            {replyingTo?.id === ticket.id && (
                                <div className="p-6 bg-muted/80 border-t border-border animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-sm font-black text-primary uppercase tracking-wider flex items-center gap-2">
                                                <Send className="h-3.5 w-3.5" />
                                                Compose Email Response
                                            </h4>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-muted-foreground h-6 px-2 text-[10px] font-bold hover:text-red-400"
                                                onClick={() => setReplyingTo(null)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 p-2 px-3 bg-card rounded-lg border border-border text-xs text-muted-foreground">
                                                <span className="font-bold">To:</span> {ticket.email || ticket.user?.email}
                                            </div>
                                            <textarea
                                                className="w-full bg-card border border-border rounded-xl p-4 text-foreground text-sm focus:ring-1 focus:ring-primary outline-none transition-all min-h-[150px] font-sans"
                                                placeholder="Write your response here... (Professional, helpful, and concise)"
                                                value={replyMessage}
                                                onChange={(e) => setReplyMessage(e.target.value)}
                                            />
                                        </div>

                                        <div className="flex justify-end gap-3">
                                            <div className="flex-1 flex items-start gap-2 text-[11px] text-muted-foreground italic bg-primary/5 p-3 rounded-lg border border-primary/10">
                                                <Info className="h-4 w-4 text-primary shrink-0" />
                                                Replying will send an email from {brand.email} and automatically mark this ticket as CLOSED.
                                            </div>
                                            <Button
                                                disabled={!replyMessage.trim() || sendingReply}
                                                onClick={handleSendReply}
                                                className="bg-primary hover:bg-primary/90 text-white font-black px-6 shadow-lg shadow-primary/20"
                                            >
                                                {sendingReply ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="mr-2 h-4 w-4" />
                                                        Send Response
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
